import type { AffiliateStockListing, CapiProductPage } from 'types/capi/pdp';
import type { Price } from 'types/capi/shared';

import type { ContractStockStatus, StockStatus } from '~/domain/stockStatus';
import { getStockStatus, toContractStockStatus } from '~/domain/stockStatus';

import { sizeSixVariants } from '../domain/sizes';

export type RetailerOption = {
  available_sizes: string[];
  position: number;
  price_currency: string;
  price_regular: number;
  price_sale?: number;
  retailer_id?: string;
  stock_status?: StockStatus;
};

export type WireRetailerOption = Omit<RetailerOption, 'stock_status'> & {
  stock_status?: ContractStockStatus;
};

function hasSize(selectedSize: string, sizes: string[]) {
  if (selectedSize === '6')
    return sizeSixVariants.some((size) => sizes.includes(size));

  return sizes.includes(selectedSize);
}

export function getStockStatusForSize(
  listing?: AffiliateStockListing,
  size?: string
): StockStatus {
  if (!listing) return 'sold-out';

  const stockStatus = getStockStatus(listing.stock_status);

  if (!stockStatus) return 'sold-out';

  if (stockStatus === 'in-stock' && size && !hasSize(size, listing.sizes))
    return 'sold-out';

  return stockStatus;
}

export function getSortedStockListingsForSize(
  product: CapiProductPage,
  size?: string
): AffiliateStockListing[] {
  if (!size) return product.stock_listings.affiliates;

  return product.stock_listings.affiliates.slice().sort((a, b) => {
    const aVal = getStockStatusForSize(a, size) === 'in-stock' ? 1 : 0;
    const bVal = getStockStatusForSize(b, size) === 'in-stock' ? 1 : 0;

    return bVal - aVal;
  });
}

export function getPriceForSize(
  listing: AffiliateStockListing,
  size?: string
): Price | undefined {
  const price = listing.size_data.find((d) => d.size === size)?.price;

  if (!price) return;

  return {
    currency: listing.price.currency,
    regular: price,
    sale: listing.price.sale
  };
}

export function toRetailerOption(
  listing: AffiliateStockListing,
  index: number,
  selectedSize?: string
): RetailerOption {
  const price = getPriceForSize(listing, selectedSize) ?? listing.price;

  return {
    available_sizes: listing.sizes,
    position: index + 1,
    price_currency: price.currency,
    price_regular: price.regular ?? -1,
    price_sale: price.sale ?? undefined,
    retailer_id: listing.affiliate.canonical_id,
    stock_status: getStockStatusForSize(listing, selectedSize)
  };
}

export function toWireRetailerOption({
  stock_status,
  ...option
}: RetailerOption): WireRetailerOption {
  return {
    ...option,
    stock_status: stock_status ? toContractStockStatus(stock_status) : undefined
  };
}

export function getCheapestListing(listing: AffiliateStockListing) {
  const cheapestListing = listing.size_data
    .filter(({ price }) => price)
    .sort((a, b) => {
      return a.price - b.price;
    })[0];

  if (!cheapestListing) return;

  return getPriceForSize(listing, cheapestListing.size);
}
